import { clientFetch, url } from "@/components/shared/fetch-client/fetch_client";
import ReadSessionWrapper from "@/components/shared/read-session-wrapper";
import { status_enum } from "@/components/shared/utils";
import { FormikWrapper } from "@/components/shared/wrappers/formik-wrapper";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingButton from "@mui/lab/LoadingButton";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import DialogActions from "@mui/material/DialogActions";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import * as Yup from "yup";
import DialogWrapper from "../../../shared/dialog/dialog-wrapper";
import FormField from "../../../shared/form/form_field";
import * as validation from "../../../shared/validation";

export default function UserDeleteDialog({ target_user, rows, open, on_close, set_rows }) {
	/* ---------------------------------- HOOKS --------------------------------- */
	const { t } = useTranslation();

	const [loading, setLoading] = useState(false);
	const [isLastAdmin, setIsLastAdmin] = useState(false);
	const [deleteLastAdmin, setDeleteLastAdmin] = useState(true);

	/* ------------------------------ FORMIK STUFF ------------------------------ */
	const formValues = {
		otp_code: "",
	};
	const formValidation = Yup.object().shape({
		otp_code: validation.inputOtp(t),
	});

	const getUsersFromDebtor = useCallback(async () => {
		const { status, body } = await clientFetch(
			url`/api/back-office/debtor/${target_user.debtor_id}/users`,
			{ params: { limit: -1, offset: 0 } },
		).get();

		if (status !== 200) {
			return null;
		}
		const adminUsers = body.data.filter((user) => user.is_admin === true);

		if (adminUsers.length === 1) {
			setIsLastAdmin(true);
			setDeleteLastAdmin(false);
		} else {
			setIsLastAdmin(false);
			setDeleteLastAdmin(true);
		}
	}, [target_user.debtor_id]);

	useEffect(() => {
		if (open && target_user.is_admin) {
			getUsersFromDebtor();
		}
	}, [open, target_user, getUsersFromDebtor]);

	const handleChange = (event) => {
		setDeleteLastAdmin(event.target.checked);
	};

	/* --------------------------- DELETE USER HANDLER -------------------------- */
	const deleteUserHandler = async (values, { setFieldError }) => {
		setLoading(true);
		setFieldError("otp_code", null);

		const { status, body } = await clientFetch(url`/api/back-office/user/${target_user.user_id}`, {
			params: { otp_code: values.otp_code },
		}).delete();
		switch (status) {
			case 202:
				// Filter removed user
				set_rows(
					rows.map((v) =>
						v.user_id === target_user.user_id ? { ...v, disabled: !v.disabled } : v,
					),
				);

				// Close dialog
				on_close();
				set_status(status_enum.SUCCESS);

				// Notify
				toast.success(t("back_office.customers.user_management.dialog.delete.success_alert"));

				break;
			case 401: {
				setLoading(false);

				// When invalid 2FA
				if (body?.detail === "Invalid OTP") {
					setFieldError(
						"otp_code",
						t("back_office.customers.user_management.dialog.delete.otp_invalid"),
					);
					return;
				}

				return;
			}

			default:
				toast.error(t("validation.errors.unknown"));
				break;
		}
	};

	return (
		<ReadSessionWrapper>
			{({ user }) => (
				<DialogWrapper
					title={t("back_office.customers.user_management.dialog.delete.title")}
					open={open}
					on_close={on_close}
					close_button
				>
					<FormikWrapper
						initialValues={formValues}
						validationSchema={formValidation}
						onSubmit={deleteUserHandler}
					>
						<Typography>
							<Trans
								i18nKey="back_office.customers.user_management.dialog.delete.description"
								values={{ username: target_user.username }}
							/>
						</Typography>

						{isLastAdmin ? (
							<FormControlLabel
								sx={{ "& .MuiTypography-root": { fontSize: 14 }, py: 1 }}
								control={<Checkbox checked={deleteLastAdmin} onChange={handleChange} />}
								label={
									<Trans
										i18nKey="back_office.customers.user_management.dialog.delete.delete_last_admin"
										values={{ debtor: target_user?.debtor_code }}
									/>
								}
							/>
						) : null}

						{user.otp_enabled ? (
							<>
								<Typography sx={{ mb: 0.5, mt: 1 }} fontWeight={500}>
									{t("inputs.otp.label")}
								</Typography>

								<FormField
									name="otp_code"
									type="text"
									size="medium"
									label=""
									placeholder={t("inputs.otp.placeholder")}
									auto_focus
									show_error_on_exit
								/>
							</>
						) : (
							<Alert variant="outlined" severity="info" sx={{ mt: 1 }}>
								{t("back_office.customers.user_management.dialog.delete.otp_not_enabled")}
							</Alert>
						)}

						<Divider sx={{ mb: 1, mt: 2 }} />

						<DialogActions sx={{ mb: 1, px: 0 }}>
							<Button variant="outlined" type="button" onClick={on_close} fullWidth>
								{t("buttons.cancel")}
							</Button>

							<LoadingButton
								variant="outlined"
								color="error"
								disabled={!(user.otp_enabled && deleteLastAdmin)}
								type="submit"
								loading={loading}
								startIcon={<DeleteIcon />}
								disableElevation
								fullWidth
							>
								{t("buttons.delete")}
							</LoadingButton>
						</DialogActions>
					</FormikWrapper>
				</DialogWrapper>
			)}
		</ReadSessionWrapper>
	);
}
